import React, { useState, useEffect, useMemo } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { contextEngine } from '../../lib/context-engine';
import { format, isToday, addHours } from 'date-fns';
import { 
  Brain, 
  Clock, 
  Users, 
  Target, 
  Lightbulb, 
  Calendar,
  AlertTriangle,
  TrendingUp,
  Sparkles,
  ChevronRight,
  CheckCircle,
  Star,
  MessageSquare,
  BookOpen,
  Coffee,
  Zap,
  MapPin,
  Settings
} from 'lucide-react';
import ViewSwitcher from '../Shared/ViewSwitcher';
import DeveloperTestingPanel from './DeveloperTestingPanel';
import './IntelligenceDashboard.css';

/**
 * PaperSedge Contextual Intelligence Dashboard
 * 
 * An advanced, context-aware dashboard that understands family patterns
 * and provides intelligent suggestions at optimal times.
 */
export default function IntelligenceDashboard() {
  const { 
    currentUser, 
    family, 
    familyCalendar, 
    familyActivities,
    messages,
    tasks,
    shoppingItems,
    plans 
  } = useFamilyContext();

  const [context, setContext] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [expandedSuggestion, setExpandedSuggestion] = useState(null);
  const [contextOverride, setContextOverride] = useState(null);

  const isParent = currentUser?.isParent;
  const userType = isParent ? 'parent' : 'teen';

  // Build contextual intelligence
  useEffect(() => {
    const buildIntelligence = async () => {
      if (!currentUser) return;

      setIsLoading(true);
      try {
        const familyData = {
          family,
          familyCalendar,
          familyActivities,
          messages,
          tasks,
          shoppingItems,
          plans
        };

        const currentContext = await contextEngine.buildContext(currentUser.id, familyData);
        const finalContext = contextOverride || currentContext;
        const contextualSuggestions = contextEngine.generateSuggestions(finalContext, userType, familyData);

        setContext(finalContext);
        setSuggestions(contextualSuggestions);
        setLastUpdate(new Date());
      } catch (error) {
        console.error('Error building intelligence context:', error);
      } finally {
        setIsLoading(false);
      }
    };

    buildIntelligence();
    
    // Refresh context every 15 minutes
    const interval = setInterval(buildIntelligence, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser, family, familyCalendar, familyActivities, messages, tasks, shoppingItems, plans, userType]);

  // Smart greeting based on context
  const smartGreeting = useMemo(() => {
    if (!context) return 'Hello';
    
    const { temporal } = context;
    const timeGreeting = temporal.suggestions[0] || `Good ${temporal.period}`;
    const personalTouch = isParent 
      ? `Ready to coordinate your family's day?` 
      : `Time to make today productive!`;
    
    return {
      main: `${timeGreeting}, ${currentUser?.name || 'there'}!`,
      subtitle: personalTouch,
      energy: temporal.energy,
      focus: temporal.focus
    };
  }, [context, currentUser, isParent]);

  const handleSuggestionAction = (suggestion) => {
    // Track user interaction for learning
    contextEngine.trackUserPattern(currentUser.id, 'suggestion-accepted', {
      temporal: context.temporal,
      suggestionType: suggestion.type
    });

    // Handle different suggestion actions
    switch (suggestion.action) {
      case 'open-big3-planner':
        // Navigate to Big 3 planner
        break;
      case 'view-messages':
        // Navigate to messages
        break;
      case 'view-tasks':
        // Navigate to tasks
        break;
      case 'view-shopping':
        // Navigate to shopping
        break;
      default:
        console.log('Suggestion action:', suggestion.action);
    }
  };

  const dismissSuggestion = (suggestion) => {
    contextEngine.trackUserPattern(currentUser.id, 'suggestion-dismissed', {
      temporal: context.temporal,
      suggestionType: suggestion.type
    });
    
    setSuggestions(prev => prev.filter(s => s !== suggestion));
  };

  const handleContextOverride = (newContext) => {
    setContextOverride(newContext);
    const contextualSuggestions = contextEngine.generateSuggestions(newContext, userType, {
      family, familyCalendar, familyActivities, messages, tasks, shoppingItems, plans
    });
    setSuggestions(contextualSuggestions);
  };

  const handleRefresh = () => {
    setContextOverride(null);
    // Trigger re-build by changing a dependency
    setLastUpdate(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="relative mx-auto w-16 h-16 mb-4">
              <div className="absolute inset-0 bg-indigo-600 rounded-full animate-ping opacity-75"></div>
              <div className="relative bg-indigo-600 rounded-full w-16 h-16 flex items-center justify-center">
                <Brain className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className="text-xl font-medium text-stone-800 mb-2">Analyzing Context...</h2>
            <p className="text-stone-600">Building your personalized intelligence dashboard</p>
          </div>
        </div>
      </div>
    );
  }

  if (!context) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-medium text-stone-800 mb-2">Context Unavailable</h2>
            <p className="text-stone-600">Unable to build contextual intelligence</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100">
      {/* Header with smart greeting and context */}
      <div className="bg-white shadow-sm border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="bg-indigo-100 p-3 rounded-xl">
                <Brain className="w-8 h-8 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-stone-900">Intelligence Dashboard</h1>
                <p className="text-stone-600">Contextual insights for your family</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <ViewSwitcher />
              <div className="text-right">
                <div className="text-sm text-stone-500">Last updated</div>
                <div className="text-sm font-medium text-stone-700">
                  {lastUpdate ? format(lastUpdate, 'h:mm a') : 'Never'}
                </div>
              </div>
            </div>
          </div>

          {/* Smart Greeting */}
          <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl p-6 border border-indigo-100">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-stone-900 mb-1">
                  {smartGreeting.main}
                </h2>
                <p className="text-stone-600 mb-3">{smartGreeting.subtitle}</p>
                
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />
                    <span className="text-stone-700">
                      Energy: <span className="font-medium capitalize">{smartGreeting.energy}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-emerald-500" />
                    <span className="text-stone-700">
                      Focus: <span className="font-medium capitalize">{smartGreeting.focus}</span>
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <div className="text-2xl font-bold text-indigo-600">
                  {format(new Date(), 'h:mm')}
                </div>
                <div className="text-sm text-stone-500 capitalize">
                  {context.temporal.period}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Primary Intelligence Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contextual Suggestions */}
            <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
              <div className="p-6 border-b border-stone-100">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-indigo-600" />
                  <h3 className="text-lg font-semibold text-stone-900">Smart Suggestions</h3>
                  <span className="bg-indigo-100 text-indigo-700 text-xs font-medium px-2 py-1 rounded-full">
                    {suggestions.length} active
                  </span>
                </div>
              </div>

              <div className="divide-y divide-stone-100">
                {suggestions.length === 0 ? (
                  <div className="p-6 text-center">
                    <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                    <h4 className="font-medium text-stone-900 mb-1">All caught up!</h4>
                    <p className="text-stone-600 text-sm">No urgent suggestions right now</p>
                  </div>
                ) : (
                  suggestions.map((suggestion, index) => (
                    <SuggestionCard
                      key={index}
                      suggestion={suggestion}
                      isExpanded={expandedSuggestion === index}
                      onExpand={() => setExpandedSuggestion(expandedSuggestion === index ? null : index)}
                      onAction={() => handleSuggestionAction(suggestion)}
                      onDismiss={() => dismissSuggestion(suggestion)}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Family Context Overview */}
            <FamilyContextCard context={context.family} userType={userType} />
          </div>

          {/* Context Sidebar */}
          <div className="space-y-6">
            {/* Temporal Context */}
            <TemporalContextCard context={context.temporal} />
            
            {/* Academic Context */}
            {!isParent && (
              <AcademicContextCard context={context.academic} />
            )}
            
            {/* Personal Patterns */}
            <PersonalPatternsCard context={context.personal} />
          </div>
        </div>
      </div>

      {/* Debug Information (Development Only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="max-w-6xl mx-auto px-6 pb-8">
          <details className="bg-stone-100 rounded-lg p-4">
            <summary className="cursor-pointer text-sm font-medium text-stone-700">
              Debug: Context Data
            </summary>
            <pre className="mt-2 text-xs text-stone-600 overflow-auto">
              {JSON.stringify(context, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {/* Developer Testing Panel */}
      <DeveloperTestingPanel
        context={context}
        onContextOverride={handleContextOverride}
        onRefresh={handleRefresh}
      />
    </div>
  );
}

// Individual card components
function SuggestionCard({ suggestion, isExpanded, onExpand, onAction, onDismiss }) {
  const priorityColors = {
    critical: 'border-l-red-500 bg-red-50',
    high: 'border-l-amber-500 bg-amber-50',
    medium: 'border-l-azure-500 bg-azure-50',
    low: 'border-l-stone-500 bg-stone-50'
  };

  const priorityIcons = {
    critical: AlertTriangle,
    high: Clock,
    medium: Lightbulb,
    low: MessageSquare
  };

  const Icon = priorityIcons[suggestion.priority];

  return (
    <div className={`p-4 border-l-4 ${priorityColors[suggestion.priority]}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <Icon className="w-5 h-5 mt-0.5 text-stone-600" />
          <div className="flex-1">
            <h4 className="font-medium text-stone-900 mb-1">{suggestion.title}</h4>
            <p className="text-stone-600 text-sm mb-2">{suggestion.description}</p>
            
            {isExpanded && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-stone-200">
                <div className="text-xs text-stone-500 mb-2">Why this suggestion?</div>
                <div className="text-sm text-stone-700">{suggestion.reasoning}</div>
                <div className="mt-2 flex items-center gap-4 text-xs text-stone-500">
                  <span>Timing: {suggestion.timing}</span>
                  <span>Priority: {suggestion.priority}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={onExpand}
            className="p-1 hover:bg-white rounded transition-colors"
          >
            <ChevronRight className={`w-4 h-4 text-stone-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          </button>
          <button
            onClick={onAction}
            className="px-3 py-1 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 transition-colors"
          >
            Action
          </button>
        </div>
      </div>
    </div>
  );
}

function FamilyContextCard({ context, userType }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <Users className="w-5 h-5 text-emerald-600" />
        <h3 className="text-lg font-semibold text-stone-900">Family Context</h3>
      </div>

      <div className="space-y-4">
        <div>
          <h4 className="font-medium text-stone-800 mb-2">Today's Events</h4>
          {context.todayEvents.length === 0 ? (
            <p className="text-stone-600 text-sm">No events scheduled</p>
          ) : (
            <div className="space-y-2">
              {context.todayEvents.slice(0, 3).map((event, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-stone-400" />
                  <span className="text-stone-700">{event.title}</span>
                  {event.time && <span className="text-stone-500">at {event.time}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {context.coordination.length > 0 && (
          <div>
            <h4 className="font-medium text-stone-800 mb-2">Coordination Opportunities</h4>
            <div className="space-y-2">
              {context.coordination.slice(0, 2).map((coord, index) => (
                <div key={index} className="text-sm text-stone-600">
                  {coord.title}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TemporalContextCard({ context }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <Clock className="w-5 h-5 text-azure-600" />
        <h3 className="text-lg font-semibold text-stone-900">Time Context</h3>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-stone-600">Period</span>
          <span className="font-medium text-stone-900 capitalize">{context.period}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-stone-600">Energy</span>
          <span className="font-medium text-stone-900 capitalize">{context.energy}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-stone-600">Focus</span>
          <span className="font-medium text-stone-900 capitalize">{context.focus}</span>
        </div>
        
        <div className="mt-4 pt-3 border-t border-stone-100">
          <h4 className="font-medium text-stone-800 mb-2">Optimal For</h4>
          <div className="flex flex-wrap gap-1">
            {context.optimalFor.map((activity, index) => (
              <span key={index} className="px-2 py-1 bg-azure-100 text-azure-700 text-xs rounded-full">
                {activity.replace('-', ' ')}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AcademicContextCard({ context }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <BookOpen className="w-5 h-5 text-violet-600" />
        <h3 className="text-lg font-semibold text-stone-900">Academic Context</h3>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-stone-600">Load</span>
          <span className={`font-medium capitalize ${
            context.academicLoad === 'high' ? 'text-red-600' : 
            context.academicLoad === 'moderate' ? 'text-amber-600' : 'text-emerald-600'
          }`}>
            {context.academicLoad}
          </span>
        </div>
        
        {context.upcomingDeadlines.length > 0 && (
          <div>
            <h4 className="font-medium text-stone-800 mb-2">Upcoming Deadlines</h4>
            <div className="space-y-1">
              {context.upcomingDeadlines.slice(0, 3).map((deadline, index) => (
                <div key={index} className="text-sm text-stone-600">
                  {deadline.title} - {format(new Date(deadline.date), 'MMM d')}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PersonalPatternsCard({ context }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <TrendingUp className="w-5 h-5 text-indigo-600" />
        <h3 className="text-lg font-semibold text-stone-900">Your Patterns</h3>
      </div>

      <div className="space-y-3">
        <div>
          <span className="text-stone-600 text-sm">Productivity Peaks</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {context.productivityPeaks.map((peak, index) => (
              <span key={index} className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                {peak.replace('-', ' ')}
              </span>
            ))}
          </div>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-stone-600">Completion Rate</span>
          <span className="font-medium text-stone-900">
            {Math.round(context.completionRate * 100)}%
          </span>
        </div>
      </div>
    </div>
  );
}